/**
 * IXVerify v2 — slim in-wizard header (ixv2 WP-3.2; plan §4).
 *
 * The dispense wizard is the one allowed full-screen surface (R7) — no
 * AppBar; instead: exit ✕, step dots, site name. Safe-area aware
 * (viewport-fit=cover phones).
 */
import { X } from 'lucide-react';
import type { WizardStep } from '../../lib/captureMachine';

const STEP_DOTS: Array<{ key: string; matches: WizardStep[] }> = [
  { key: 'capture', matches: ['start', 'capture'] },
  { key: 'review', matches: ['review'] },
  { key: 'attest', matches: ['attest'] },
  { key: 'done', matches: ['published', 'mismatch', 'closed'] },
];

export default function WizardHeader({
  step,
  siteName,
  onExit,
}: {
  step: WizardStep;
  siteName: string | null;
  onExit: () => void;
}) {
  const activeIdx = STEP_DOTS.findIndex((d) => d.matches.includes(step));
  return (
    <header
      className="flex h-12 flex-shrink-0 items-center justify-between gap-2 border-b border-gray-800 bg-gray-950 px-3 text-white"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <button
        type="button"
        aria-label="Exit dispense"
        onClick={onExit}
        className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-white/80 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none sm:h-9 sm:w-9"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="flex items-center gap-1.5" aria-label="Dispense progress" role="img">
        {STEP_DOTS.map((d, i) => (
          <span
            key={d.key}
            className={`h-1.5 rounded-full transition-all duration-150 motion-reduce:transition-none ${
              i === activeIdx ? 'w-6 bg-primary-500' : i < activeIdx ? 'w-1.5 bg-primary-500/60' : 'w-1.5 bg-white/25'
            }`}
          />
        ))}
      </div>

      <div className="max-w-[40%] truncate text-right text-xs text-white/70">{siteName || ''}</div>
    </header>
  );
}
